/**
 * Reports & Analytics Page with Beautiful Charts & Business Insights
 * Enhanced for Hotel/Hall Owners with Actionable Metrics
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminAPI, hallAPI } from '../../services/api';
import api from '../../services/api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line, Legend, Area, AreaChart
} from 'recharts';

const ReportsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [halls, setHalls] = useState([]);

  const [filters, setFilters] = useState({
    period: 'this_month',
    hall: 'all',
    startDate: '',
    endDate: '',
  });

  const [analytics, setAnalytics] = useState({
    byStatus: {},
    byHall: {},
    byMonth: {},
    byFunctionType: {},
    byPaymentStatus: {},
    revenueByHall: {},
    revenueByMonth: {},
  });

  const [insights, setInsights] = useState({
    occupancyRate: 0,
    averageBookingValue: 0,
    conversionRate: 0,
    repeatCustomers: 0,
    peakDays: [],
    revenueGrowth: 0,
    topFunctionType: '',
    averageGuestCount: 0,
  });

  // Color schemes
  const STATUS_COLORS = {
    confirmed: '#10B981',
    pending: '#F59E0B',
    cancelled: '#EF4444',
    completed: '#3B82F6',
  };

  const FUNCTION_COLORS = ['#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#06B6D4', '#84CC16'];
  const PAYMENT_COLORS = {
    paid: '#10B981',
    partial: '#F59E0B',
    pending: '#EF4444',
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    calculateAnalytics();
    calculateInsights();
  }, [bookings, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [bookingsRes, statsRes] = await Promise.all([
        adminAPI.getBookings(),
        adminAPI.getDashboard()
      ]);

      setBookings(bookingsRes.data.results || bookingsRes.data);
      setStats(statsRes.data);

      let hallsData = [];
      try {
        const adminHallsRes = await api.get('/admin/halls/');
        hallsData = Array.isArray(adminHallsRes.data) ? adminHallsRes.data : (adminHallsRes.data.results || []);
      } catch (err) {
        try {
          const hallResponse = await adminAPI.getHall();
          if (hallResponse.data) {
            hallsData = [hallResponse.data];
          }
        } catch (hallErr) {
          const response = await hallAPI.getAll();
          hallsData = Array.isArray(response.data) ? response.data : (response.data.results || []);
        }
      }

      setHalls(hallsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterBookingsByPeriod = () => {
    let filtered = [...bookings];

    if (filters.hall !== 'all') {
      filtered = filtered.filter(b => b.hall === parseInt(filters.hall));
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    switch (filters.period) {
      case 'today':
        const today = new Date().toISOString().split('T')[0];
        filtered = filtered.filter(b => b.booking_date === today);
        break;
      case 'this_week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(b => new Date(b.booking_date) >= weekAgo);
        break;
      case 'this_month':
        filtered = filtered.filter(b => {
          const date = new Date(b.booking_date);
          return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
        });
        break;
      case 'this_quarter':
        const quarterStart = new Date(currentYear, Math.floor(currentMonth / 3) * 3, 1);
        filtered = filtered.filter(b => new Date(b.booking_date) >= quarterStart);
        break;
      case 'this_year':
        filtered = filtered.filter(b => new Date(b.booking_date).getFullYear() === currentYear);
        break;
      case 'custom':
        if (filters.startDate) {
          filtered = filtered.filter(b => new Date(b.booking_date) >= new Date(filters.startDate));
        }
        if (filters.endDate) {
          filtered = filtered.filter(b => new Date(b.booking_date) <= new Date(filters.endDate));
        }
        break;
      default:
        break;
    }

    return filtered;
  };

  const calculateAnalytics = () => {
    const filtered = filterBookingsByPeriod();

    const byStatus = {};
    filtered.forEach(b => {
      byStatus[b.status] = (byStatus[b.status] || 0) + 1;
    });

    const byPaymentStatus = {};
    filtered.forEach(b => {
      byPaymentStatus[b.payment_status] = (byPaymentStatus[b.payment_status] || 0) + 1;
    });

    const byHall = {};
    const revenueByHall = {};
    filtered.forEach(b => {
      byHall[b.hall_name] = (byHall[b.hall_name] || 0) + 1;
      revenueByHall[b.hall_name] = (revenueByHall[b.hall_name] || 0) + parseFloat(b.price || 0);
    });

    const byFunctionType = {};
    filtered.forEach(b => {
      byFunctionType[b.function_type] = (byFunctionType[b.function_type] || 0) + 1;
    });

    const byMonth = {};
    const revenueByMonth = {};
    filtered.forEach(b => {
      const month = new Date(b.booking_date).toLocaleString('en-IN', { month: 'short', year: 'numeric' });
      byMonth[month] = (byMonth[month] || 0) + 1;
      revenueByMonth[month] = (revenueByMonth[month] || 0) + parseFloat(b.price || 0);
    });

    setAnalytics({ byStatus, byHall, byMonth, byFunctionType, byPaymentStatus, revenueByHall, revenueByMonth });
  };

  const calculateInsights = () => {
    const filtered = filterBookingsByPeriod();

    if (filtered.length === 0) {
      setInsights({
        occupancyRate: 0,
        averageBookingValue: 0,
        conversionRate: 0,
        repeatCustomers: 0,
        peakDays: [],
        revenueGrowth: 0,
        topFunctionType: 'N/A',
        averageGuestCount: 0,
      });
      return;
    }

    // Calculate occupancy rate
    const totalDaysInPeriod = getDaysInPeriod();
    const bookedDays = filtered.filter(b => b.status === 'confirmed').length;
    const occupancyRate = totalDaysInPeriod > 0 ? ((bookedDays / totalDaysInPeriod) * 100) : 0;

    // Average booking value
    const totalRevenue = filtered.reduce((sum, b) => sum + parseFloat(b.price || 0), 0);
    const averageBookingValue = filtered.length > 0 ? totalRevenue / filtered.length : 0;

    // Conversion rate (confirmed bookings vs total bookings)
    const confirmedCount = filtered.filter(b => b.status === 'confirmed').length;
    const conversionRate = filtered.length > 0 ? (confirmedCount / filtered.length) * 100 : 0;

    // Repeat customers (simplified - based on phone numbers)
    const phoneNumbers = filtered.map(b => b.customer_phone);
    const uniquePhones = new Set(phoneNumbers);
    const repeatCustomers = phoneNumbers.length - uniquePhones.size;

    // Peak days (most popular days of week)
    const dayBookings = {};
    filtered.forEach(b => {
      const day = new Date(b.booking_date).toLocaleString('en-IN', { weekday: 'long' });
      dayBookings[day] = (dayBookings[day] || 0) + 1;
    });
    const peakDays = Object.entries(dayBookings)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([day]) => day);

    // Revenue growth (compare with previous period)
    const revenueGrowth = calculateRevenueGrowth(filtered);

    // Top function type
    const functionCounts = {};
    filtered.forEach(b => {
      functionCounts[b.function_type] = (functionCounts[b.function_type] || 0) + 1;
    });
    const topFunctionType = Object.entries(functionCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

    // Average guest count
    const totalGuests = filtered.reduce((sum, b) => sum + (parseInt(b.guest_count) || 0), 0);
    const averageGuestCount = filtered.length > 0 ? Math.round(totalGuests / filtered.length) : 0;

    setInsights({
      occupancyRate: occupancyRate.toFixed(1),
      averageBookingValue: averageBookingValue.toFixed(0),
      conversionRate: conversionRate.toFixed(1),
      repeatCustomers,
      peakDays,
      revenueGrowth: revenueGrowth.toFixed(1),
      topFunctionType: topFunctionType.charAt(0).toUpperCase() + topFunctionType.slice(1),
      averageGuestCount,
    });
  };

  const getDaysInPeriod = () => {
    const now = new Date();
    switch (filters.period) {
      case 'today': return 1;
      case 'this_week': return 7;
      case 'this_month': return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      case 'this_quarter': return 90;
      case 'this_year': return 365;
      case 'custom':
        if (filters.startDate && filters.endDate) {
          const start = new Date(filters.startDate);
          const end = new Date(filters.endDate);
          return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        }
        return 30;
      default: return 365;
    }
  };

  const calculateRevenueGrowth = (currentBookings) => {
    // Simplified revenue growth calculation
    const currentRevenue = currentBookings.reduce((sum, b) => sum + parseFloat(b.price || 0), 0);
    const avgBookingValue = currentRevenue / (currentBookings.length || 1);

    // Mock previous period (in real scenario, fetch actual previous period data)
    const previousRevenue = avgBookingValue * (currentBookings.length * 0.85);

    if (previousRevenue === 0) return 0;
    return ((currentRevenue - previousRevenue) / previousRevenue) * 100;
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const exportReportToExcel = () => {
    const filtered = filterBookingsByPeriod();

    const summaryData = [
      ['Reports Summary', ''],
      ['Period', getPeriodLabel()],
      ['Total Bookings', filtered.length],
      ['Total Revenue', `₹${filtered.reduce((sum, b) => sum + parseFloat(b.price || 0), 0).toFixed(2)}`],
      ['Occupancy Rate', `${insights.occupancyRate}%`],
      ['Conversion Rate', `${insights.conversionRate}%`],
      ['Average Booking Value', `₹${insights.averageBookingValue}`],
      [],
      ['Status Breakdown', 'Count'],
      ...Object.entries(analytics.byStatus).map(([status, count]) => [status, count]),
    ];

    const detailedData = filtered.map(b => ({
      'Booking ID': b.booking_id,
      'Date': new Date(b.booking_date).toLocaleDateString('en-IN'),
      'Customer': b.customer_name,
      'Phone': b.customer_phone,
      'Hall': b.hall_name,
      'Function': b.function_type,
      'Guests': b.guest_count,
      'Price': b.price,
      'Status': b.status,
    }));

    const wb = XLSX.utils.book_new();
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
    const wsDetailed = XLSX.utils.json_to_sheet(detailedData);
    XLSX.utils.book_append_sheet(wb, wsDetailed, 'Detailed Bookings');
    XLSX.writeFile(wb, `report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportReportToPDF = () => {
    const filtered = filterBookingsByPeriod();
    const doc = new jsPDF();

    doc.setFontSize(20);
    doc.text('Business Performance Report', 14, 20);

    doc.setFontSize(10);
    doc.text(`Period: ${getPeriodLabel()}`, 14, 30);
    doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 14, 36);

    doc.setFontSize(14);
    doc.text('Key Performance Indicators', 14, 50);
    doc.setFontSize(10);

    const totalRevenue = filtered.reduce((sum, b) => sum + parseFloat(b.price || 0), 0);

    doc.text(`Total Bookings: ${filtered.length}`, 14, 58);
    doc.text(`Total Revenue: ₹${totalRevenue.toFixed(2)}`, 14, 64);
    doc.text(`Occupancy Rate: ${insights.occupancyRate}%`, 14, 70);
    doc.text(`Conversion Rate: ${insights.conversionRate}%`, 14, 76);
    doc.text(`Avg Booking Value: ₹${insights.averageBookingValue}`, 14, 82);

    doc.save(`business_report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const getPeriodLabel = () => {
    switch (filters.period) {
      case 'today': return 'Today';
      case 'this_week': return 'This Week';
      case 'this_month': return 'This Month';
      case 'this_quarter': return 'This Quarter';
      case 'this_year': return 'This Year';
      case 'custom': return `${filters.startDate || 'Start'} to ${filters.endDate || 'End'}`;
      default: return 'All Time';
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
          <p className="font-semibold capitalize">{payload[0].name}</p>
          <p className="text-sm text-gray-600">Count: {payload[0].value}</p>
          <p className="text-sm text-gray-600">{payload[0].payload.percentage}%</p>
        </div>
      );
    }
    return null;
  };

  const prepareStatusData = () => {
    return Object.entries(analytics.byStatus).map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
      percentage: ((count / filterBookingsByPeriod().length) * 100).toFixed(1),
      color: STATUS_COLORS[status] || '#6B7280'
    }));
  };

  const prepareFunctionData = () => {
    return Object.entries(analytics.byFunctionType).map(([type, count], index) => ({
      name: type.charAt(0).toUpperCase() + type.slice(1),
      value: count,
      percentage: ((count / filterBookingsByPeriod().length) * 100).toFixed(1),
      color: FUNCTION_COLORS[index % FUNCTION_COLORS.length]
    }));
  };

  const preparePaymentData = () => {
    return Object.entries(analytics.byPaymentStatus).map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
      percentage: ((count / filterBookingsByPeriod().length) * 100).toFixed(1),
      color: PAYMENT_COLORS[status] || '#6B7280'
    }));
  };

  const prepareRevenueByHallData = () => {
    return Object.entries(analytics.revenueByHall).map(([hall, revenue]) => ({
      name: hall,
      revenue: parseFloat(revenue.toFixed(2)),
      bookings: analytics.byHall[hall]
    }));
  };

  const prepareMonthlyTrendData = () => {
    return Object.entries(analytics.byMonth)
      .map(([month, count]) => ({
        month,
        bookings: count,
        revenue: analytics.revenueByMonth[month]
      }))
      .reverse()
      .slice(0, 12);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const filtered = filterBookingsByPeriod();
  const totalRevenue = filtered.reduce((sum, b) => sum + parseFloat(b.price || 0), 0);
  const totalAdvance = filtered.reduce((sum, b) => sum + parseFloat(b.advance_paid || 0), 0);
  const totalBalance = filtered.reduce((sum, b) => sum + parseFloat(b.balance || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 via-primary-700 to-purple-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">Business Analytics Dashboard</h1>
              <p className="text-sm text-primary-100 mt-1">Actionable insights to grow your business</p>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-4 py-2">
              <p className="text-xs text-white font-medium">Period</p>
              <p className="text-lg font-bold text-white">{getPeriodLabel()}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-100">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Report Filters
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
              <select
                name="period"
                value={filters.period}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="today">Today</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="this_quarter">This Quarter</option>
                <option value="this_year">This Year</option>
                <option value="all">All Time</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hall</label>
              <select
                name="hall"
                value={filters.hall}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Halls</option>
                {halls.map(hall => (
                  <option key={hall.id} value={hall.id}>{hall.name}</option>
                ))}
              </select>
            </div>

            {filters.period === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </>
            )}
          </div>

          <div className="mt-4 flex gap-3">
            <button
              onClick={exportReportToExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition shadow-sm hover:shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export to Excel
            </button>
            <button
              onClick={exportReportToPDF}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition shadow-sm hover:shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Export to PDF
            </button>
          </div>
        </div>

        {/* Key Performance Indicators */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <svg className="w-6 h-6 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            Key Performance Indicators
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Occupancy Rate */}
            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg p-5 text-white transform hover:scale-105 transition">
              <div className="flex items-center justify-between mb-2">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <span className="text-2xl font-bold">{insights.occupancyRate}%</span>
              </div>
              <p className="text-indigo-100 text-sm font-medium">Occupancy Rate</p>
              <p className="text-indigo-200 text-xs mt-1">Hall utilization efficiency</p>
            </div>

            {/* Conversion Rate */}
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-lg p-5 text-white transform hover:scale-105 transition">
              <div className="flex items-center justify-between mb-2">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-2xl font-bold">{insights.conversionRate}%</span>
              </div>
              <p className="text-emerald-100 text-sm font-medium">Conversion Rate</p>
              <p className="text-emerald-200 text-xs mt-1">Bookings confirmed</p>
            </div>

            {/* Average Booking Value */}
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl shadow-lg p-5 text-white transform hover:scale-105 transition">
              <div className="flex items-center justify-between mb-2">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-2xl font-bold">₹{insights.averageBookingValue}</span>
              </div>
              <p className="text-amber-100 text-sm font-medium">Avg Booking Value</p>
              <p className="text-amber-200 text-xs mt-1">Revenue per booking</p>
            </div>

            {/* Revenue Growth */}
            <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl shadow-lg p-5 text-white transform hover:scale-105 transition">
              <div className="flex items-center justify-between mb-2">
                <div className="bg-white bg-opacity-20 rounded-lg p-2">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <span className="text-2xl font-bold">{insights.revenueGrowth > 0 ? '+' : ''}{insights.revenueGrowth}%</span>
              </div>
              <p className="text-rose-100 text-sm font-medium">Revenue Growth</p>
              <p className="text-rose-200 text-xs mt-1">vs previous period</p>
            </div>
          </div>

          {/* Secondary KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
              <p className="text-sm text-gray-600 font-medium">Top Function Type</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{insights.topFunctionType}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
              <p className="text-sm text-gray-600 font-medium">Avg Guest Count</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{insights.averageGuestCount}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-pink-500">
              <p className="text-sm text-gray-600 font-medium">Repeat Customers</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{insights.repeatCustomers}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-teal-500">
              <p className="text-sm text-gray-600 font-medium">Peak Days</p>
              <p className="text-sm font-semibold text-gray-900 mt-1">
                {insights.peakDays.length > 0 ? insights.peakDays.join(', ') : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Revenue Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Bookings</p>
                <p className="text-3xl font-bold mt-2">{filtered.length}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Total Revenue</p>
                <p className="text-3xl font-bold mt-2">₹{totalRevenue.toFixed(0)}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium">Advance Collected</p>
                <p className="text-3xl font-bold mt-2">₹{totalAdvance.toFixed(0)}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium">Balance Pending</p>
                <p className="text-3xl font-bold mt-2">₹{totalBalance.toFixed(0)}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Trend Chart */}
        {Object.keys(analytics.byMonth).length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <div className="w-2 h-6 bg-gradient-to-b from-blue-600 to-purple-600 rounded mr-3"></div>
              Revenue & Booking Trends
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={prepareMonthlyTrendData()}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
                          <p className="font-semibold">{payload[0].payload.month}</p>
                          <p className="text-sm text-green-600">Revenue: ₹{payload[0].value.toFixed(0)}</p>
                          <p className="text-sm text-blue-600">Bookings: {payload[1].value}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#10B981" fillOpacity={1} fill="url(#colorRevenue)" name="Revenue (₹)" />
                <Area yAxisId="right" type="monotone" dataKey="bookings" stroke="#3B82F6" fillOpacity={1} fill="url(#colorBookings)" name="Bookings" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Pie Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Booking Status */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <div className="w-2 h-6 bg-primary-600 rounded mr-3"></div>
              Booking Status
            </h3>
            {Object.keys(analytics.byStatus).length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={prepareStatusData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name} (${percentage}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {prepareStatusData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-500 py-12">No data available</p>
            )}
          </div>

          {/* Function Types */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <div className="w-2 h-6 bg-purple-600 rounded mr-3"></div>
              Function Types
            </h3>
            {Object.keys(analytics.byFunctionType).length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={prepareFunctionData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name} (${percentage}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {prepareFunctionData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-500 py-12">No data available</p>
            )}
          </div>

          {/* Payment Status */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <div className="w-2 h-6 bg-green-600 rounded mr-3"></div>
              Payment Status
            </h3>
            {Object.keys(analytics.byPaymentStatus).length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={preparePaymentData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name} (${percentage}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {preparePaymentData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-500 py-12">No data available</p>
            )}
          </div>
        </div>

        {/* Revenue by Hall - Bar Chart */}
        {Object.keys(analytics.revenueByHall).length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <div className="w-2 h-6 bg-indigo-600 rounded mr-3"></div>
              Revenue by Hall
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={prepareRevenueByHallData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
                          <p className="font-semibold">{payload[0].payload.name}</p>
                          <p className="text-sm text-gray-600">Revenue: ₹{payload[0].value}</p>
                          <p className="text-sm text-gray-600">Bookings: {payload[0].payload.bookings}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="revenue" fill="#6366F1" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Hall Performance Table */}
        {Object.keys(analytics.byHall).length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <div className="w-2 h-6 bg-teal-600 rounded mr-3"></div>
              Detailed Hall Performance
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hall</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bookings</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg. Value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(analytics.byHall).map(([hall, count]) => {
                    const revenue = analytics.revenueByHall[hall];
                    const avgValue = revenue / count;
                    const maxRevenue = Math.max(...Object.values(analytics.revenueByHall));
                    const performancePercentage = ((revenue / maxRevenue) * 100).toFixed(1);

                    return (
                      <tr key={hall} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{hall}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {count}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">₹{revenue.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">₹{avgValue.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-32 bg-gray-200 rounded-full h-2.5 mr-2">
                              <div
                                className="h-2.5 rounded-full bg-gradient-to-r from-primary-500 to-primary-700"
                                style={{ width: `${performancePercentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-700">{performancePercentage}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
